import * as mutate from "@script/mutate";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type * as type from "@vTypes";
import { format } from "date-fns";
import type React from "react";
import { useEffect, useState } from "react";
import { toast } from "@/components/toast/CustomToast";
import ButtonInitial from "@/components/ui/button/btn-initial";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import httpClient from "@/config/axios";
import { QUERY_KEYS } from "@/constants/QUERY_KEYS";
import { useWantedStatusQuery } from "@/hooks/roster-craete/useWantedStatusQuery";
import { useLoginUser } from "@/hooks/useAuth";
import { cn } from "@/utils/cn";

interface HeadNurseHeaderProps {
  wantedButtonRef?: React.RefObject<HTMLButtonElement | null>;
  isSubmitRequest: boolean;
  isFinalSubmitButtonClick: boolean;
  activeDate: { year: number; month: number } | null;
  wantedList: type.WantedAll[] | undefined;
  setActiveDate: (date: { year: number; month: number } | null) => void;
  selectedGroupId?: string | null;
  showMonthSelect?: boolean;
  showMonthLabel?: boolean;
  showDeadlineControl?: boolean;
}

export default function HeadNurseHeader({
  wantedButtonRef,
  isSubmitRequest,
  isFinalSubmitButtonClick,
  activeDate,
  wantedList,
  setActiveDate,
  selectedGroupId,
  showMonthSelect = true,
  showMonthLabel = true,
  showDeadlineControl = true,
}: HeadNurseHeaderProps) {
  const loginUser = useLoginUser();
  const isHeadNurse = loginUser?.is_head_nurse;
  const queryClient = useQueryClient();

  const gid = selectedGroupId ?? "";

  const { data: wantedStatus } = useWantedStatusQuery({
    year: activeDate?.year ?? null,
    month: activeDate?.month ?? null,
    groupId: gid,
    enabled:
      isHeadNurse &&
      showDeadlineControl &&
      !!activeDate?.year &&
      !!activeDate?.month,
  });

  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [deadlineDate, setDeadlineDate] = useState<Date | null>(null);

  useEffect(() => {
    if (wantedStatus?.exp_date) {
      const date =
        typeof wantedStatus.exp_date === "string"
          ? new Date(wantedStatus.exp_date)
          : wantedStatus.exp_date instanceof Date
            ? wantedStatus.exp_date
            : new Date(wantedStatus.exp_date);

      if (!Number.isNaN(date.getTime())) {
        setDeadlineDate(date);
      } else {
        setDeadlineDate(null);
      }
    } else {
      setDeadlineDate(null);
    }
  }, [wantedStatus?.exp_date]);

  useEffect(() => {
    if (datePickerOpen) {
      if (wantedStatus?.exp_date) {
        setDeadlineDate(
          wantedStatus.exp_date instanceof Date
            ? wantedStatus.exp_date
            : new Date(wantedStatus.exp_date)
        );
      } else {
        setDeadlineDate(null);
      }
    }
  }, [datePickerOpen, wantedStatus?.exp_date]);

  const { mutate: requestWantedMutation, isPending: isRequestPending } =
    useMutation({
      mutationFn: async () => {
        if (!activeDate?.year || !activeDate?.month) return;

        if (wantedStatus?.status === "requested") {
          await mutate.saveChangeWantedDeadline(
            activeDate.year,
            activeDate.month,
            deadlineDate,
            gid
          );
        } else {
          await mutate.requestWantedDeadline(
            activeDate.year,
            activeDate.month,
            deadlineDate,
            gid
          );
        }
      },
      onSuccess: async () => {
        if (!activeDate?.year || !activeDate?.month) return;

        toast.success(
          wantedStatus?.status === "requested"
            ? "원티드 마감일이 변경되었습니다."
            : "원티드 요청되었습니다."
        );

        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.status(
            activeDate.year,
            activeDate.month,
            gid
          ),
        });

        await queryClient.invalidateQueries({ queryKey: ["alarms"] });
        setDatePickerOpen(false);
      },
    });

  const { mutate: closeWantedMutation, isPending: isClosePending } =
    useMutation({
      mutationFn: async () => {
        if (!activeDate?.year || !activeDate?.month) return;

        const params = new URLSearchParams({
          year: String(activeDate.year),
          month: String(activeDate.month),
        });

        if (gid) {
          params.append("group_id", gid);
        }

        await httpClient.patch(`/wanted/close?${params.toString()}`);
      },
      onSuccess: async () => {
        if (!activeDate?.year || !activeDate?.month) return;

        alert("원티드 요청이 마감되었습니다.");

        await queryClient.invalidateQueries({
          queryKey: QUERY_KEYS.wanted.status(
            activeDate.year,
            activeDate.month,
            gid
          ),
        });

        await queryClient.invalidateQueries({ queryKey: ["alarms"] });
      },
      onError: (error) => {
        alert("에러가 발생했습니다.");
        console.error(error);
      },
    });

  const handleRequestSubmit = () => {
    if (!activeDate?.year || !activeDate?.month) return;
    if (!confirm("요청하시겠습니까")) return;
    requestWantedMutation();
  };

  const handleCloseWanted = () => {
    if (!activeDate?.year || !activeDate?.month) return;
    if (!confirm("원티드 요청을 마감하시겠습니까?")) return;
    closeWantedMutation();
  };

  const isSubmitting = isRequestPending || isClosePending;

  return (
    <div className="flex flex-wrap items-center justify-end gap-2">
      {showDeadlineControl && isHeadNurse && (
        <div className="order-2">
          <TooltipProvider>
            <Tooltip open={datePickerOpen ? false : undefined}>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild>
                  <TooltipTrigger asChild>
                    <ButtonInitial
                      ref={wantedButtonRef}
                      onClick={() => {
                        setDatePickerOpen(true);
                      }}
                      disabled={isSubmitRequest || isFinalSubmitButtonClick}
                      aria-label="원티드 제출 변경, 마감일 선택"
                      className={cn(
                        "min-h-9 rounded-md border border-sig bg-background px-3 py-1.5 text-sm font-semibold text-sig transition-colors hover:bg-row-hover"
                      )}
                    >
                      원티드 제출 변경
                    </ButtonInitial>
                  </TooltipTrigger>
                </PopoverTrigger>
                {(wantedStatus?.status === "requested" ||
                  wantedStatus?.status === "closed") && (
                  <TooltipContent
                    side="bottom"
                    className="bg-gray-800 text-white"
                  >
                    <div className="text-xs">
                      {wantedStatus.exp_date
                        ? `마감일: ${format(
                            typeof wantedStatus.exp_date === "string"
                              ? new Date(wantedStatus.exp_date)
                              : wantedStatus.exp_date instanceof Date
                                ? wantedStatus.exp_date
                                : new Date(wantedStatus.exp_date),
                            "yyyy년 MM월 dd일"
                          )}`
                        : "마감일 없음"}
                    </div>
                  </TooltipContent>
                )}
                <PopoverContent
                  className="w-auto p-0"
                  align="center"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="flex flex-col">
                    {/* 캘린더 - 바로 펼쳐져 있음 */}
                    <div className="px-4">
                      <Calendar
                        mode="single"
                        className="font-pretendard!"
                        selected={deadlineDate || undefined}
                        onSelect={(date) => {
                          if (date) {
                            setDeadlineDate(date);
                          } else {
                            setDeadlineDate(null);
                          }
                        }}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const compareDate = new Date(date);
                          compareDate.setHours(0, 0, 0, 0);
                          return compareDate < today;
                        }}
                        captionLayout="label"
                        defaultMonth={
                          activeDate?.year && activeDate?.month
                            ? new Date(activeDate.year, activeDate.month - 1, 1)
                            : undefined
                        }
                        formatters={{
                          formatCaption: (date) => {
                            return `${date.getFullYear()}년 ${date.getMonth() + 1}월`;
                          },
                        }}
                      />
                    </div>

                    {/* 구분선 */}
                    <div className="border-t border-[#D6D6DE] mx-4 my-2" />

                    {/* 마감일 없음 토글 */}
                    <div className="px-4 pb-4">
                      <div className="flex items-center justify-between">
                        <span className="text-[14px] font-pretendard text-[#333333]">
                          마감일 없음
                        </span>
                        <Switch
                          checked={!deadlineDate}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setDeadlineDate(null);
                            } else {
                              setDeadlineDate(new Date());
                            }
                          }}
                        />
                      </div>
                    </div>

                    {/* 요청하기 버튼 */}
                    <div className="px-4 pb-4 flex flex-col gap-2">
                      {wantedStatus?.status === "requested" && (
                        <button
                          type="button"
                          onClick={handleCloseWanted}
                          disabled={isSubmitting}
                          className="w-full px-4 text-center  py-3 text-sig border border-sig rounded-[10px]  transition-colors font-pretendard! text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          원티드 요청 마감하기
                        </button>
                      )}

                      <button
                        type="button"
                        onClick={handleRequestSubmit}
                        disabled={isSubmitting}
                        className="w-full px-4 text-center  py-4 bg-sig text-white rounded-[10px] hover:bg-sig/90 transition-colors font-pretendard! text-[15px] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmitting
                          ? "요청 중..."
                          : wantedStatus?.status === "requested" ||
                              wantedStatus?.status === "closed"
                            ? "원티드 마감일 변경하기"
                            : "요청하기"}
                      </button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
      {showMonthSelect && activeDate && (
        <div className="order-1 flex items-center gap-2">
          {showMonthLabel && (
            <span className="text-xs font-medium text-text-sub">작성 월</span>
          )}
          <Select
            value={`${activeDate.year}-${activeDate.month}`}
            onValueChange={(value) => {
              const [year, month] = value.split("-").map(Number);
              setActiveDate({ year, month });
            }}
          >
            <SelectTrigger
              aria-label="작성 월 선택"
              className="h-8 min-w-36 border-border bg-background px-2 text-sm font-medium"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {wantedList && wantedList.length > 0
                ? wantedList.map((item) => {
                    const value = `${item.year}-${item.month}`;
                    const isClosed = item.status === "closed";
                    return (
                      <SelectItem
                        key={value}
                        value={value}
                        className={isClosed ? "opacity-60 text-[#939399]" : ""}
                      >
                        <span className="text-base font-semibold">
                          {item.year}년 {item.month}월{isClosed && " (마감)"}
                        </span>
                      </SelectItem>
                    );
                  })
                : Array.from({ length: 5 }, (_, yearIdx) => {
                    const year = new Date().getFullYear() - 2 + yearIdx;
                    return Array.from({ length: 12 }, (_, monthIdx) => {
                      const month = monthIdx + 1;
                      const value = `${year}-${month}`;
                      return (
                        <SelectItem key={value} value={value}>
                          <span className="text-sm">
                            {year}년 {month}월
                          </span>
                        </SelectItem>
                      );
                    });
                  })}
            </SelectContent>
          </Select>
        </div>
      )}
    </div>
  );
}
